import { api, ApiError } from "../api/client";
import { store } from "../state";
import { escapeHtml } from "./html";

/**
 * Curadoria das sugestoes pendentes, dentro da conversa.
 *
 * Fica na coluna do chat de proposito: o usuario ensina, a sugestao aparece logo abaixo, ele
 * aprova e o no nasce no grafo ao lado. Manter a cadeia causal visivel numa tela so e o que
 * torna a base de conhecimento compreensivel — mandar o usuario a outra tela para aprovar
 * quebraria justamente o elo que a tela existe para mostrar.
 *
 * Aprovar e o UNICO caminho pelo qual conhecimento entra no Grafo Cognitivo (ADR-015). Nao ha
 * atalho aqui, e nao deve haver.
 */
export class SuggestionsTray {
  constructor(
    private readonly root: HTMLElement,
    private readonly onCurated: () => void
  ) {
    this.root.addEventListener("click", (event) => {
      const target = (event.target as HTMLElement).closest<HTMLButtonElement>("button[data-action]");
      if (!target) {
        return;
      }
      const { action, id } = target.dataset;
      if (action && id) {
        void this.curate(id, action === "approve" ? "approve" : "reject");
      }
    });

    store.subscribe(() => this.render());
    this.render();
  }

  async load(): Promise<void> {
    try {
      const suggestions = await api.listPendingSuggestions(store.state.userId);
      store.update({ suggestions });
    } catch {
      // Curadoria indisponivel nao pode derrubar a conversa; a bandeja apenas nao aparece.
      store.update({ suggestions: [] });
    }
  }

  private async curate(id: string, action: "approve" | "reject"): Promise<void> {
    store.update({ suggestionsBusy: [...store.state.suggestionsBusy, id] });
    try {
      if (action === "approve") {
        await api.approveSuggestion(id);
      } else {
        await api.rejectSuggestion(id);
      }

      // Remocao otimista apos sucesso confirmado: a sugestao ja nao esta pendente no servidor.
      store.update({ suggestions: store.state.suggestions.filter((item) => item.id !== id) });
      this.onCurated();
    } catch (error) {
      const detail = error instanceof ApiError ? error.message : "Não foi possível concluir.";
      this.showError(id, detail);
    } finally {
      store.update({ suggestionsBusy: store.state.suggestionsBusy.filter((busy) => busy !== id) });
    }
  }

  private showError(id: string, detail: string): void {
    const card = this.root.querySelector<HTMLElement>(`[data-card="${id}"] .suggestion-error`);
    if (card) {
      card.textContent = detail;
      card.classList.remove("hidden");
    }
  }

  private render(): void {
    const { suggestions, suggestionsBusy } = store.state;
    if (suggestions.length === 0) {
      this.root.innerHTML = "";
      this.root.classList.remove("has-items");
      return;
    }

    this.root.classList.add("has-items");
    this.root.innerHTML = `
      <div class="tray-head">
        <strong>${suggestions.length} ${suggestions.length === 1 ? "sugestão pendente" : "sugestões pendentes"}</strong>
        <span class="muted">Aprovar grava no Grafo Cognitivo.</span>
      </div>
      ${suggestions.map((suggestion) => this.renderCard(suggestion, suggestionsBusy.includes(suggestion.id))).join("")}
    `;
  }

  private renderCard(
    suggestion: import("../api/types").SuggestedMemoryRecord,
    busy: boolean
  ): string {
    const disabled = busy ? "disabled" : "";
    // Substituicao e adicao sao operacoes diferentes e precisam parecer diferentes. Um cartao de
    // correcao apresentado como adicao ja custou conhecimento: aprovar "o nome da esposa e
    // Ashley" obsoletou o nome do proprio usuario sem que a tela dissesse uma palavra sobre isso.
    const replaces = suggestion.operationType === "Correct" || suggestion.operationType === "Update";
    const target = suggestion.relatedMemoryTitle;

    const warning = replaces
      ? `<p class="suggestion-replaces">
           Substitui ${target ? `<strong>${escapeHtml(target)}</strong>` : "um registro existente"},
           que passa a obsoleto.
         </p>`
      : "";

    return `
      <article class="suggestion ${replaces ? "replaces" : ""}" data-card="${escapeHtml(suggestion.id)}">
        <header>
          <strong>${escapeHtml(suggestion.title)}</strong>
          <span class="chip quiet">${escapeHtml(suggestion.scope)}</span>
        </header>
        ${replaces ? `<span class="chip warn">correção</span>` : ""}
        <p>${escapeHtml(suggestion.summary || suggestion.content)}</p>
        ${warning}
        <p class="suggestion-error hidden"></p>
        <div class="suggestion-actions">
          <button class="${replaces ? "danger-button" : "primary-button"} small" data-action="approve" data-id="${escapeHtml(suggestion.id)}" ${disabled}>
            ${busy ? "…" : replaces ? "Substituir" : "Aprovar"}
          </button>
          <button class="secondary-button small" data-action="reject" data-id="${escapeHtml(suggestion.id)}" ${disabled}>
            Rejeitar
          </button>
        </div>
      </article>
    `;
  }
}
